import React from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { CountUp } from "../intro/IntroStats";

const sceneMotion = {
  initial: { opacity: 0, scale: 0.94, filter: "blur(10px)" },
  animate: { opacity: 1, scale: 1, filter: "blur(0px)" },
  exit: { opacity: 0, scale: 1.08, filter: "blur(12px)" },
  transition: { duration: 0.5, ease: "easeOut" },
};

function Kicker({ children, accent }) {
  return (
    <motion.div
      initial={{ opacity: 0, letterSpacing: "0.7em" }}
      animate={{ opacity: 1, letterSpacing: "0.34em" }}
      transition={{ duration: 0.7 }}
      className="font-mono text-[11px] uppercase text-center mb-8"
      style={{ color: accent }}
    >
      {children}
    </motion.div>
  );
}

export function PhaseTrackScene({ phases, accent }) {
  return (
    <motion.div {...sceneMotion} data-testid="veroogle-outro-phases" className="w-full max-w-5xl">
      <Kicker accent={accent}>// The engine, end to end</Kicker>
      <div className="relative">
        <div className="absolute left-[10%] right-[10%] top-6 h-[2px] bg-white/10 hidden sm:block" />
        <motion.div
          className="absolute left-[10%] top-6 h-[2px] hidden sm:block origin-left"
          style={{ right: "10%", background: `linear-gradient(90deg, ${accent}, #10b981)`, boxShadow: `0 0 18px ${accent}` }}
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 0.3, duration: 1.7, ease: "easeInOut" }}
        />
        <div className="relative grid grid-cols-1 sm:grid-cols-6 gap-4 sm:gap-2">
          {phases.map((p, i) => (
            <motion.div
              key={p}
              className="flex sm:flex-col items-center gap-3 sm:gap-2 text-left sm:text-center"
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35 + i * 0.34, type: "spring", stiffness: 160, damping: 16 }}
            >
              <motion.div
                className="w-12 h-12 rounded-full flex items-center justify-center border-2 bg-[#03060F] shrink-0"
                initial={{ borderColor: "rgba(255,255,255,0.15)" }}
                animate={{ borderColor: "#10b981", boxShadow: ["0 0 0 0 rgba(16,185,129,0)", "0 0 0 14px rgba(16,185,129,0.25)", "0 0 0 0 rgba(16,185,129,0)"] }}
                transition={{ delay: 0.5 + i * 0.34, duration: 0.8 }}
              >
                <Check className="w-5 h-5 text-emerald-400" />
              </motion.div>
              <div>
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">Phase 0{i + 1}</div>
                <div className="text-sm sm:text-[13px] font-semibold text-white leading-snug">{p}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </motion.div>
  );
}

export function SessionScene({ session, accent }) {
  const items = [
    { k: "leads", label: "Merchants found", value: session.leads },
    { k: "pipeline", label: "Worked in the pipeline", value: session.pipeline },
    { k: "approved", label: "Onboarded & approved", value: session.approved },
    { k: "recovered", label: "Recovered for you", value: session.recovered, prefix: "$" },
  ];
  return (
    <motion.div {...sceneMotion} data-testid="veroogle-outro-session" className="w-full max-w-4xl">
      <Kicker accent={accent}>// What we just did, live</Kicker>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-6 sm:gap-8">
        {items.map((it, i) => (
          <motion.div
            key={it.k}
            data-testid={`veroogle-outro-session-${it.k}`}
            className="text-center"
            initial={{ opacity: 0, y: 26, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ delay: 0.15 + i * 0.16, type: "spring", stiffness: 170, damping: 15 }}
          >
            <div className="font-mono text-4xl sm:text-5xl font-bold text-white tabular-nums">
              {it.prefix}<CountUp to={it.value || 0} duration={1300} />
            </div>
            <div className="mt-2 font-mono text-[10px] tracking-[0.2em] uppercase text-slate-400">{it.label}</div>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
}

export function SavingsScene({ annual, name, accent }) {
  return (
    <motion.div {...sceneMotion} data-testid="veroogle-outro-savings" className="text-center">
      <Kicker accent={accent}>// What {name} keeps, every year</Kicker>
      <motion.div
        className="relative inline-block"
        initial={{ scale: 0.6 }}
        animate={{ scale: [0.6, 1.08, 1] }}
        transition={{ duration: 1.1, times: [0, 0.7, 1], ease: "easeOut" }}
      >
        <div aria-hidden className="absolute -inset-10 rounded-full blur-3xl opacity-60" style={{ background: `radial-gradient(circle, ${accent}66, transparent 70%)` }} />
        <div className="relative font-mono font-black text-6xl sm:text-8xl lg:text-9xl tabular-nums bg-gradient-to-b from-white via-emerald-200 to-emerald-400 bg-clip-text text-transparent">
          $<CountUp to={annual || 0} duration={1800} />
        </div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.2 }}
        className="mt-4 font-mono text-xs tracking-[0.3em] uppercase text-slate-300"
      >
        saved every year with Nexengines
      </motion.div>
    </motion.div>
  );
}
